import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HeaderProvider } from './contexts/HeaderContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { lazyPage } from './routing/lazyPage';
import { useRouteHead } from './routing/useRouteHead';
import NotFound from './pages/NotFound';

// Public pages: loaded ahead of the first render for the address being opened (see lazyPage).
const Home = lazyPage(() => import('./pages/Home'), /^\/$/);
const About = lazyPage(() => import('./pages/About'), /^\/about$/);
const Contact = lazyPage(() => import('./pages/Contact'), /^\/contact$/);
const Articles = lazyPage(() => import('./pages/Articles'), /^\/articles$/);
const ForAuthors = lazyPage(() => import('./pages/ForAuthors'), /^\/for-authors$/);
const ForReviewers = lazyPage(() => import('./pages/ForReviewers'), /^\/for-reviewers$/);
const EditorialBoard = lazyPage(() => import('./pages/EditorialBoard'), /^\/editorial-board$/);
const EditorialProcess = lazy(() => import('./pages/EditorialProcess'));
const ManuscriptSubmissionForm = lazyPage(() => import('./pages/ManuscriptSubmissionForm'), /^\/manuscript-submission$/);
const ArticleDetail = lazyPage(() => import('./pages/ArticleDetail'), /^\/articles\/[^/]+$/);
const Policies = lazyPage(() => import('./pages/Policies'), /^\/guidelines$/);
const PeerReviewPolicy = lazyPage(() => import('./pages/PeerReviewPolicy'), /^\/peer-review-policy$/);
const OpenAccessStatement = lazyPage(() => import('./pages/OpenAccessStatement'), /^\/open-access-statement$/);
const JournalArchives = lazyPage(() => import('./pages/JournalArchives'), /^\/journal-archives$/);

// The InDesign / JATS production tool, for Production Editors.
const EditorProtectedRoute = lazy(() => import('./components/editor/EditorProtectedRoute'));
import { EditorProvider } from './contexts/EditorContext'
const EditorManuscriptTool = lazy(() => import('./pages/editor/EditorManuscriptTool'));

// The one login for the administrator, the three editorial roles and reviewers (authors have their own).
const UnifiedLogin = lazy(() => import('./pages/unified/UnifiedLogin'));
const UnifiedDashboard = lazy(() => import('./pages/unified/UnifiedDashboard'));
// Editorial Desk: the three dashboards and the manuscript screen. It is where every editorial role works.
const DeskShell = lazy(() => import('./pages/desk/DeskShell'));
const DeskArea = lazy(() => import('./pages/desk/DeskArea'));
const ManuscriptWorkspace = lazy(() => import('./pages/desk/ManuscriptWorkspace'));
// Development-only catalogue of the admin design system's building blocks.
const DesignSystemPreview = lazy(() => import('./pages/admin/DesignSystemPreview'));
import { UnifiedAuthProvider } from './contexts/UnifiedAuthContext';

// Import Author Dashboard Components
const AuthorLogin = lazy(() => import('./pages/author/AuthorLogin'));
const AuthorRegister = lazy(() => import('./pages/author/AuthorRegister'));
const AuthorReset = lazy(() => import('./pages/author/AuthorReset'));
const AuthorOnboarding = lazy(() => import('./pages/author/AuthorOnboarding'));
const AuthorDashboard = lazy(() => import('./pages/author/AuthorDashboard'));
const DashboardOverview = lazy(() => import('./pages/author/DashboardOverview'));
const AuthorSubmission = lazy(() => import('./pages/author/AuthorSubmission'));
const AuthorDrafts = lazy(() => import('./pages/author/AuthorDrafts'));
const SubmissionList = lazy(() => import('./pages/author/SubmissionList'));
const SubmissionDetail = lazy(() => import('./pages/author/SubmissionDetail'));
const SubmissionSuccess = lazy(() => import('./pages/author/SubmissionSuccess'));
const RevisionSubmission = lazy(() => import('./pages/author/RevisionSubmission'));
const GalleyProofReview = lazy(() => import('./pages/author/GalleyProofReview'));

// Import Reviewer Components
const ReviewerLogin = lazy(() => import('./pages/reviewer/ReviewerLogin'));
const ReviewerDashboard = lazy(() => import('./pages/reviewer/ReviewerDashboard'));
const ReviewerInvitationResponse = lazy(() => import('./pages/reviewer/ReviewerInvitationResponse'));
const ReviewerFirstLogin = lazy(() => import('./pages/reviewer/ReviewerFirstLogin'));
const ReviewerForgotPassword = lazy(() => import('./pages/reviewer/ReviewerForgotPassword'));
const ReviewDetail = lazy(() => import('./pages/reviewer/ReviewDetail'));
const MyReviews = lazy(() => import('./pages/reviewer/MyReviews'));
const ReviewerPasswordReset = lazy(() => import('./pages/reviewer/ReviewerPasswordReset'));
const ReviewSubmissionForm = lazy(() => import('./pages/reviewer/ReviewSubmissionForm'));
const ReviewerProfile = lazy(() => import('./pages/reviewer/ReviewerProfile'));
const ReviewerMessaging = lazy(() => import('./components/reviewer/ReviewerMessaging'));

// Import Admin Dashboard Components
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboardHome = lazy(() => import('./pages/admin/AdminDashboardHome'));
const Submissions = lazy(() => import('./pages/admin/Submissions'));
const SubmissionDetailLayout = lazy(() => import('./pages/admin/SubmissionDetailLayout'));
const SubmissionOverviewTab = lazy(() => import('./pages/admin/SubmissionOverviewTab'));
const SubmissionAuthorsTab = lazy(() => import('./pages/admin/SubmissionAuthorsTab'));
const SubmissionPlagiarismTab = lazy(() => import('./pages/admin/SubmissionPlagiarismTab'));
const SubmissionReviewersTab = lazy(() => import('./pages/admin/SubmissionReviewersTab'));
const SubmissionHistoryTab = lazy(() => import('./pages/admin/SubmissionHistoryTab'));
const Reviewers = lazy(() => import('./pages/admin/Reviewers'));
const ManuscriptReviewers = lazy(() => import('./pages/admin/ManuscriptReviewers'));
const SubmissionsBatchManager = lazy(() => import('./components/admin/SubmissionsBatchManager'));
const Editors = lazy(() => import('./pages/admin/Editors'));
const Analytics = lazy(() => import('./pages/admin/Analytics'));
const EmailTemplates = lazy(() => import('./pages/admin/EmailTemplates'));
const EmailManagement = lazy(() => import('./pages/admin/EmailManagement'));
const AssignEditors = lazy(() => import('./pages/admin/AssignEditors'));
const AdminUsers = lazy(() => import('./pages/admin/Users'));
const AdminSettings = lazy(() => import('./pages/admin/Settings'));
const ContentEditor = lazy(() => import('./pages/admin/ContentEditor'));
const AnnouncementEditor = lazy(() => import('./pages/admin/AnnouncementEditor'));

// Reviewer Management Components
const ReviewerCommunicationHub = lazy(() => import('./components/admin/ReviewerCommunicationHub'));
const COIManagementDashboard = lazy(() => import('./components/admin/COIManagementDashboard'));
const AdminReviewManagement = lazy(() => import('./components/admin/AdminReviewManagement'));
const EditorialDecisions = lazy(() => import('./components/admin/EditorialDecisions'));
const ManuscriptTool = lazy(() => import('./pages/admin/ManuscriptTool'));
const EditorialBoardManager = lazy(() => import('./pages/admin/EditorialBoardManager'));
const AuthorProfile = lazy(() => import('./pages/author/AuthorProfile'));
const PublicationList = lazy(() => import('./pages/author/PublicationList'));
const PublicationDetail = lazy(() => import('./pages/author/PublicationDetail'));

// Plagiarism Review Components
const PlagiarismReview = lazy(() => import('./pages/admin/PlagiarismReview'));

const VolumeManager = lazy(() => import('../src/components/admin/VolumeManager'));
const IssueManager = lazy(() => import('../src/components/admin/IssueManager'));
const ArticleManager = lazy(() => import('../src/components/admin/ArticleManager'));

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // canonical, robots and sign-in page titles for this address
  useRouteHead(pathname);

  return null;
};

// Route-level Suspense fallback. At least a screen tall, so the footer stays below
// the fold while a page loads: a footer drawn under the navbar and then pushed down
// by the real page registers as a layout shift.
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center" aria-busy="true" aria-live="polite">
    <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
  </div>
);

// Addresses that used to hold a role's own sign-in page and dashboard. The
// journal now has one login and one place to work, so they redirect: a sign-in
// page to the login, anything else to the Editorial Desk (which sends someone
// who is not signed in to the login itself).
const RETIRED_ADDRESSES = [
  '/editor', '/editor-in-chief', '/editorial-office',
  '/managing-editor', '/associate-editor', '/section-editor', '/ethics-quality-editor', '/editorial-assistant',
];

const RetiredAddress = () => {
  const { pathname } = useLocation();
  return <Navigate to={/\/login\/?$/.test(pathname) ? '/login' : '/desk'} replace />;
};

// The public site's navbar and footer wrap every page EXCEPT the admin and
// editorial screens, which own the whole viewport and carry their own chrome.
// Those screens follow the admin light/dark theme; the public navbar and footer
// are a fixed light design, so wrapping one inside the other clashed in dark
// mode. Author and reviewer portals are reader-facing and keep the site chrome.
const FULL_SCREEN_SECTIONS = ['/desk', '/design-system', '/admin', '/dashboard', '/login', '/editor'];

// Whole path segments only, so '/editor' covers '/editor/dashboard' but not the
// public '/editorial-board' or '/editorial-process'.
const isFullScreenPath = (pathname: string) =>
  FULL_SCREEN_SECTIONS.some((section) => pathname === section || pathname.startsWith(`${section}/`));

const SiteFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const fullScreen = isFullScreenPath(pathname);

  if (fullScreen) return <>{children}</>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="pt-16">{children}</main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="app-container">
      <Router>
        <HeaderProvider>
          <UnifiedAuthProvider>
          <EditorProvider>
            <ScrollToTop />
            <SiteFrame>
                <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/articles" element={<Articles />} />
                  <Route path="/articles/:articleId" element={<ArticleDetail />} />
                  <Route path="/journal-archives" element={<JournalArchives />} />
                  <Route path="/for-authors" element={<ForAuthors />} />
                  <Route path="/for-reviewers" element={<ForReviewers />} />
                  <Route path="/editorial-board" element={<EditorialBoard />} />
                  <Route path="/editorial-process" element={<EditorialProcess />} />
                  {/* Submission Guidelines & Policies, retired: everything on it is on For Authors, Journal Policies or the Open Access Statement (item 7 of the website review) */}
                  <Route path="/disclaimer" element={<Navigate to="/for-authors" replace />} />
                  <Route path="/guidelines" element={<Policies />} />
                  <Route path="/peer-review-policy" element={<PeerReviewPolicy />} />
                  <Route path="/policies" element={<Navigate to="/guidelines" replace />} />
                  <Route path="/open-access-statement" element={<OpenAccessStatement />} />
                  <Route path="/manuscript-submission" element={<ManuscriptSubmissionForm />} />

                  {/* The InDesign / JATS production tool, for Production Editors. */}
                  <Route path="/editor/manuscript-tool" element={
                    <EditorProtectedRoute>
                      <EditorManuscriptTool />
                    </EditorProtectedRoute>
                  } />

                  {/* The one login, and the page that lists a person's workspaces. */}
                  <Route path="/login" element={<UnifiedLogin />} />
                  <Route path="/dashboard" element={<UnifiedDashboard />} />

                  {/* The per-role sign-in pages and dashboards are retired: old
                      links and bookmarks go to the one login and the Desk. */}
                  {RETIRED_ADDRESSES.map((section) => (
                    <Route key={section} path={`${section}/*`} element={<RetiredAddress />} />
                  ))}

                  {/* Editorial Desk. Auth is per-role and handled inside DeskShell,
                      which reads whichever role tokens this browser already holds. */}
                  <Route path="/desk" element={<DeskShell />}>
                    {/* The bare address shows the role's home area (the Desk works it out). */}
                    <Route index element={<DeskArea />} />
                    <Route path="manuscripts/:id" element={<ManuscriptWorkspace />} />
                    <Route path=":queueId" element={<DeskArea />} />
                  </Route>

                  {/* Design-system catalogue. Dev builds only -- never routed in production. */}
                  {import.meta.env.DEV && (
                    <Route path="/design-system" element={<DesignSystemPreview />} />
                  )}

                  {/* Author Dashboard Routes */}
                  <Route path="/author/login" element={<AuthorLogin />} />
                  <Route path="/author/register" element={<AuthorRegister />} />
                  <Route path="/author/reset-password" element={<AuthorReset />} />
                  <Route path="/author/onboarding" element={<AuthorOnboarding />} />

                  {/* Reviewer Routes */}
                  <Route path="/reviewer/login" element={<ReviewerLogin />} />
                  <Route path="/reviewer/invitation/:token" element={<ReviewerInvitationResponse />} />
                  <Route path="/reviewer/first-login/:token" element={<ReviewerFirstLogin />} />
                  <Route path="/reviewer/forgot-password" element={<ReviewerForgotPassword />} />
                  <Route path="/reviewer/reset-password" element={<ReviewerPasswordReset />} />
                  <Route path="/reviewer/reviews" element={<ReviewerDashboard />}>
                    <Route index element={<MyReviews />} />
                  </Route>
                  <Route path="/reviewer/reviews/:reviewId" element={<ReviewerDashboard />}>
                    <Route index element={<ReviewDetail />} />
                    <Route path="submit" element={<ReviewSubmissionForm />} />
                  </Route>
                  <Route path="/reviewer/profile" element={<ReviewerDashboard />}>
                    <Route index element={<ReviewerProfile />} />
                  </Route>

                  {/* Protected Reviewer Dashboard Routes */}
                  <Route path="/reviewer/dashboard" element={<ReviewerDashboard />}>
                    <Route index element={<div>Dashboard Overview</div>} />
                    <Route path="reviews" element={<MyReviews />} />
                    <Route path="reviews/:reviewId" element={<ReviewDetail />} />
                    <Route path="reviews/:reviewId/submit" element={<ReviewSubmissionForm />} />
                    <Route path="profile" element={<ReviewerProfile />} />
                    <Route path="messages" element={<ReviewerMessaging />} />
                  </Route>

                  {/* Protected Author Dashboard Routes */}
                  <Route path="/author/dashboard" element={<AuthorDashboard />}>
                    <Route index element={<DashboardOverview />} />
                    <Route path="overview" element={<DashboardOverview />} />
                    <Route path="submit" element={<AuthorSubmission />} />
                    <Route path="drafts" element={<AuthorDrafts />} />
                    <Route path="submissions" element={<SubmissionList />} />
                    <Route path="submissions/:id" element={<SubmissionDetail />} />
                    <Route path="submissions/:id/success" element={<SubmissionSuccess />} />
                    <Route path="submissions/:id/revise" element={<RevisionSubmission />} />
                    <Route path="submissions/:id/galley" element={<GalleyProofReview />} />
                    <Route path="profile" element={<AuthorProfile />} />
                    <Route path="publications" element={<PublicationList />} />
                    <Route path="publications/:id" element={<PublicationDetail />} />
                  </Route>

                  {/* New Global Admin Dashboard routes */}
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin" element={<AdminLayout><AdminDashboardHome /></AdminLayout>} />
                  <Route path="/admin/dashboard" element={<AdminLayout><AdminDashboardHome /></AdminLayout>} />

                  {/* Admin Submission Management Routes */}
                  <Route path="/admin/submissions" element={<AdminLayout><Submissions /></AdminLayout>} />
                  <Route path="/admin/submissions-batch" element={<AdminLayout><SubmissionsBatchManager /></AdminLayout>} />
                  <Route path="/admin/submissions/:id" element={<AdminLayout><SubmissionDetailLayout /></AdminLayout>}>
                    <Route index element={<Navigate to="overview" replace />} />
                    <Route path="overview" element={<SubmissionOverviewTab />} />
                    <Route path="authors" element={<SubmissionAuthorsTab />} />
                    <Route path="plagiarism" element={<SubmissionPlagiarismTab />} />
                    <Route path="reviewers" element={<SubmissionReviewersTab />} />
                    <Route path="history" element={<SubmissionHistoryTab />} />
                  </Route>
                  {/* New Admin Management Routes */}
                  {/* Volumes -> Issues -> Articles drill-down, all under AdminLayout now (formerly split off into /admin/old/*) */}
                  <Route path="/admin/issues" element={<AdminLayout><VolumeManager /></AdminLayout>} />
                  <Route path="/admin/issues/:volumeId" element={<AdminLayout><IssueManager /></AdminLayout>} />
                  <Route path="/admin/articles" element={<AdminLayout><VolumeManager /></AdminLayout>} />
                  <Route path="/admin/articles/:issueId" element={<AdminLayout><ArticleManager /></AdminLayout>} />
                  <Route path="/admin/users" element={<AdminLayout><AdminUsers /></AdminLayout>} />
                  <Route path="/admin/settings" element={<AdminLayout><AdminSettings /></AdminLayout>} />
                  <Route path="/admin/content" element={<AdminLayout><ContentEditor /></AdminLayout>} />
                  <Route path="/admin/announcement" element={<AdminLayout><AnnouncementEditor /></AdminLayout>} />
                  <Route path="/admin/reviewers" element={<AdminLayout><Reviewers /></AdminLayout>} />
                  <Route path="/admin/manuscript-reviewers" element={<AdminLayout><ManuscriptReviewers /></AdminLayout>} />
                  <Route path="/admin/editors" element={<AdminLayout><Editors /></AdminLayout>} />
                  <Route path="/admin/analytics" element={<AdminLayout><Analytics /></AdminLayout>} />
                  <Route path="/admin/email-templates" element={<AdminLayout><EmailTemplates /></AdminLayout>} />
                  <Route path="/admin/email-management" element={<AdminLayout><EmailManagement /></AdminLayout>} />
                  <Route path="/admin/assign-editors" element={<AdminLayout><AssignEditors /></AdminLayout>} />

                  {/* Reviewer Management Routes */}
                  <Route path="/admin/review-management" element={<AdminLayout><AdminReviewManagement /></AdminLayout>} />
                  <Route path="/admin/editorial-decisions" element={<AdminLayout><EditorialDecisions /></AdminLayout>} />
                  <Route path="/admin/reviewer-communication" element={<AdminLayout><ReviewerCommunicationHub /></AdminLayout>} />
                  <Route path="/admin/coi-management" element={<AdminLayout><COIManagementDashboard /></AdminLayout>} />
                  <Route path="/admin/reviewer-analytics" element={<AdminLayout><Analytics /></AdminLayout>} />
                  <Route path="/admin/manuscript-tool" element={<AdminLayout><ManuscriptTool /></AdminLayout>} />
                  <Route path="/admin/editorial-board" element={<AdminLayout><EditorialBoardManager /></AdminLayout>} />

                  {/* Plagiarism Review Routes */}
                  <Route path="/admin/plagiarism-review" element={<AdminLayout><PlagiarismReview /></AdminLayout>} />

                  {/* Anything else: a proper "page not found" (noindex) instead of a blank page */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
            </SiteFrame>
          </EditorProvider>
          </UnifiedAuthProvider>
        </HeaderProvider>
      </Router>
    </div>
  );
}

export default App;
